import { useEffect, useRef, useState } from 'react';

import { Icon } from '../icon';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { ActiveJob } from '@/hooks/useJobProgress';
import { cn } from '@/new/lib/utils';

type UploadStatus = 'idle' | 'uploading' | 'waiting' | 'processing' | 'scanning' | 'complete';

interface DisplayJob extends ActiveJob {
  isCompleted?: boolean;
}

interface StatusRowProps {
  icon: 'spinner' | 'check' | 'alert';
  text: string;
  textColor: string;
  truncateText?: boolean;
  progress?: {
    value: number;
    text: string;
    bgColor: string;
    fillColor: string;
  };
}

function StatusRow({ icon, text, textColor, truncateText, progress }: StatusRowProps) {
  const iconClass =
    icon === 'spinner'
      ? 'lucide--loader-circle animate-spin'
      : icon === 'check'
        ? 'lucide--check'
        : 'lucide--alert-circle';

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Icon className={cn(iconClass, textColor)} />
        <span className={cn('text-sm', textColor, truncateText && 'truncate')}>{text}</span>
      </div>
      {progress && (
        <div className="flex flex-1 items-center gap-2">
          <div className={cn('h-1.5 min-w-32 flex-1 overflow-hidden rounded-full', progress.bgColor)}>
            <div
              className={cn('h-full rounded-full transition-all duration-300', progress.fillColor)}
              style={{ width: `${progress.value}%` }}
            />
          </div>
          <span className={cn('text-sm whitespace-nowrap tabular-nums', textColor)}>{progress.text}</span>
        </div>
      )}
    </div>
  );
}

interface UploadStatusIndicatorProps {
  isUploading: boolean;
  uploadProgress: number;
  uploadedCount: number;
  totalCount: number;
  isScanning: boolean;
  scanComplete: boolean;
  duplicateCount: number;
  activeJobs: ActiveJob[];
  waitingForJob: boolean;
  matterId: string;
  uploadId: string | null;
}

export function UploadStatusIndicator({
  isUploading,
  uploadProgress,
  uploadedCount,
  totalCount,
  isScanning,
  scanComplete,
  duplicateCount,
  activeJobs,
  waitingForJob,
  matterId,
  uploadId,
}: UploadStatusIndicatorProps) {
  const [displayedStatus, setDisplayedStatus] = useState<UploadStatus>('idle');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousStatusRef = useRef<UploadStatus>('idle');
  const [displayJobs, setDisplayJobs] = useState<DisplayJob[]>([]);

  const hasActiveJobs = activeJobs.length > 0;

  // Fetch upload history when scan completes to get accurate file counts
  const { data: uploadHistory, refetch: refetchUploadHistory } = useGetMatterUploadHistory(matterId);

  // Get the latest upload entry that matches the current uploadId
  const currentUpload = uploadHistory?.find((upload) => upload.id === uploadId);

  // Refetch upload history when scan completes
  useEffect(() => {
    if (scanComplete && uploadId) {
      refetchUploadHistory();
    }
  }, [scanComplete, uploadId, refetchUploadHistory]);

  // Sync displayJobs with activeJobs while preserving order and marking completed jobs
  useEffect(() => {
    setDisplayJobs((prevDisplayJobs) => {
      // Update existing jobs: mark as completed if no longer active, update progress if still active
      const updatedJobs = prevDisplayJobs.map((displayJob) => {
        const activeJob = activeJobs.find((j) => j.id === displayJob.id);
        if (activeJob) {
          // Job still active - update with latest data
          return { ...activeJob, isCompleted: false };
        } else {
          // Job completed - mark as completed
          return { ...displayJob, isCompleted: true };
        }
      });

      // Add any new jobs that aren't in our display list yet
      const existingIds = new Set(prevDisplayJobs.map((j) => j.id));
      const newJobs = activeJobs
        .filter((job) => !existingIds.has(job.id))
        .map((job) => ({ ...job, isCompleted: false }));

      return [...updatedJobs, ...newJobs];
    });
  }, [activeJobs]);

  // Clear display jobs when we leave the processing state
  useEffect(() => {
    if (displayedStatus !== 'processing' && displayJobs.length > 0) {
      // Small delay to allow the transition animation
      const timeout = setTimeout(() => {
        setDisplayJobs([]);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [displayedStatus, displayJobs.length]);

  // Determine the current status based on props
  let currentStatus: UploadStatus = 'idle';
  if (isUploading) {
    currentStatus = 'uploading';
  } else if (waitingForJob) {
    currentStatus = 'waiting';
  } else if (hasActiveJobs) {
    currentStatus = 'processing';
  } else if (isScanning) {
    currentStatus = 'scanning';
  } else if (scanComplete) {
    currentStatus = 'complete';
  }

  // Handle status transitions with animation
  useEffect(() => {
    if (currentStatus !== previousStatusRef.current && currentStatus !== 'idle') {
      // Start transition animation
      setIsTransitioning(true);

      // After exit animation completes, update the displayed status
      const timeout = setTimeout(() => {
        setDisplayedStatus(currentStatus);
        setIsTransitioning(false);
      }, 150);

      previousStatusRef.current = currentStatus;
      return () => clearTimeout(timeout);
    } else if (currentStatus === 'idle' && previousStatusRef.current !== 'idle') {
      // Only reset to idle when the scan is complete (the flow has finished)
      // This prevents the indicator from disappearing during the gap between
      // upload completion and job creation for zip files
      if (scanComplete) {
        previousStatusRef.current = 'idle';
        setDisplayedStatus('idle');
      }
    } else if (currentStatus !== 'idle' && displayedStatus === 'idle') {
      // Initial transition from idle
      setDisplayedStatus(currentStatus);
      previousStatusRef.current = currentStatus;
    }
  }, [currentStatus, displayedStatus, scanComplete]);

  // Don't render anything when idle
  if (displayedStatus === 'idle' && !isTransitioning) {
    return null;
  }

  // Use upload history data if available for accurate duplicate count
  const finalDuplicateCount = currentUpload?.duplicateCount ?? duplicateCount;
  const hasDuplicates = finalDuplicateCount > 0;

  // Determine colors based on status
  const getStatusColors = () => {
    if (displayedStatus === 'uploading') {
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-300',
        text: 'text-blue-600',
        progressBg: 'bg-blue-200',
        progressFill: 'bg-blue-500',
      };
    }
    if (displayedStatus === 'waiting') {
      return {
        bg: 'bg-gray-50',
        border: 'border-gray-300',
        text: 'text-gray-600',
        progressBg: 'bg-gray-200',
        progressFill: 'bg-gray-500',
      };
    }
    if (displayedStatus === 'processing') {
      return {
        bg: 'bg-purple-50',
        border: 'border-purple-300',
        text: 'text-purple-600',
        progressBg: 'bg-purple-200',
        progressFill: 'bg-purple-500',
      };
    }
    if (displayedStatus === 'scanning') {
      return {
        bg: 'bg-green-50',
        border: 'border-green-300',
        text: 'text-green-600',
        progressBg: 'bg-green-200',
        progressFill: 'bg-green-500',
      };
    }
    if (displayedStatus === 'complete') {
      if (hasDuplicates) {
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-300',
          text: 'text-amber-600',
          progressBg: 'bg-amber-200',
          progressFill: 'bg-amber-500',
        };
      }
      return {
        bg: 'bg-green-50',
        border: 'border-green-300',
        text: 'text-green-600',
        progressBg: 'bg-green-200',
        progressFill: 'bg-green-500',
      };
    }
    return {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      text: 'text-gray-600',
      progressBg: 'bg-gray-200',
      progressFill: 'bg-gray-500',
    };
  };

  const colors = getStatusColors();

  const renderContent = () => {
    if (displayedStatus === 'uploading') {
      return (
        <StatusRow
          icon="spinner"
          text="Uploading"
          textColor={colors.text}
          progress={{
            value: uploadProgress,
            text: `${uploadedCount} / ${totalCount}`,
            bgColor: colors.progressBg,
            fillColor: colors.progressFill,
          }}
        />
      );
    }

    if (displayedStatus === 'waiting') {
      return <StatusRow icon="spinner" text="Waiting for zip to begin processing..." textColor={colors.text} />;
    }

    if (displayedStatus === 'processing') {
      if (displayJobs.length === 0) {
        return <StatusRow icon="spinner" text="Preparing..." textColor={colors.text} />;
      }
      return (
        <div className="flex max-h-40 w-full flex-col gap-2.5 overflow-y-auto">
          {displayJobs.map((job) => {
            const isCompleted = job.isCompleted;
            const progress = isCompleted
              ? 100
              : job.progressStatus === 'preparing'
                ? job.preparingProgress || 0
                : job.totalFiles === 0
                  ? 0
                  : Math.round((job.processedFiles / job.totalFiles) * 100);

            const progressText =
              job.progressStatus === 'preparing'
                ? job.preparingMessage || 'Preparing...'
                : job.totalFiles === 0
                  ? 'Preparing...'
                  : `${job.processedFiles.toLocaleString()} / ${job.totalFiles.toLocaleString()}`;

            const jobLabel =
              job.type === 'unzip' ? 'Extracting zip' : job.type === 'relativity' ? 'Relativity import' : 'Processing';

            return (
              <StatusRow
                key={job.id}
                icon={isCompleted ? 'check' : 'spinner'}
                text={`${jobLabel} - ${job.fileName}`}
                textColor={colors.text}
                truncateText
                progress={{
                  value: progress,
                  text: progressText,
                  bgColor: colors.progressBg,
                  fillColor: colors.progressFill,
                }}
              />
            );
          })}
        </div>
      );
    }

    if (displayedStatus === 'scanning') {
      return <StatusRow icon="spinner" text="Scanning for duplicates" textColor={colors.text} />;
    }

    if (displayedStatus === 'complete') {
      if (currentUpload) {
        const finalDuplicateCount = currentUpload.duplicateCount;
        const finalFilesAdded = currentUpload.successfullyUploadedFileCount;
        const hasFinalDuplicates = finalDuplicateCount > 0;

        if (hasFinalDuplicates) {
          return (
            <StatusRow
              icon="alert"
              text={`${finalDuplicateCount} duplicate${finalDuplicateCount !== 1 ? 's' : ''} detected. ${finalFilesAdded} file${finalFilesAdded !== 1 ? 's' : ''} added. View Upload Breakdown to see duplicates.`}
              textColor={colors.text}
            />
          );
        }
        return (
          <StatusRow
            icon="check"
            text={`No duplicates detected. Added ${finalFilesAdded} file${finalFilesAdded !== 1 ? 's' : ''}.`}
            textColor={colors.text}
          />
        );
      }

      return <StatusRow icon="check" text="Upload complete." textColor={colors.text} />;
    }

    return null;
  };

  return (
    <div className="my-2">
      <div
        className={cn(
          'flex min-h-9 items-center justify-between rounded-lg border p-2 px-3 transition-colors duration-200',
          colors.bg,
          colors.border,
        )}
      >
        <div
          className={cn(
            'flex w-full items-center justify-between transition-all duration-150',
            isTransitioning ? 'translate-y-[-8px] opacity-0' : 'translate-y-0 opacity-100',
          )}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
